import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Logo from '../components/Logo';
import UpgradeScreen from '../components/UpgradeScreen';
import { hasFullAccess } from '../lib/platform';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ChevronLeft, Bell, BellRing, Plus, Trash2, Loader2, Play,
  MapPin, Euro, Home, Clock, Calendar, ChevronDown, ChevronUp,
  Power, PowerOff, History, Zap, Search, X, Check, ArrowUpRight
} from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'terrain', label: 'Terrain' },
];

const FREQ_OPTIONS = [
  { value: 'daily', label: 'Quotidienne', icon: Clock },
  { value: 'weekly', label: 'Hebdomadaire', icon: Calendar },
];

const AlertsPage = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [runningId, setRunningId] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: '', ville: '', type_bien: 'appartement',
    budget_min: '', budget_max: '', surface_min: '', frequency: 'daily',
  });

  const isClub = hasFullAccess(user?.subscription_tier);

  useEffect(() => {
    if (isClub) fetchAlerts();
    else setLoading(false);
  }, [isClub]);

  if (!isClub) {
    return (
      <UpgradeScreen
        icon={BellRing}
        iconBgClass="from-amber-500 to-orange-600"
        iconColorClass="text-amber-500"
        buttonGradientClass="from-amber-500 to-orange-600"
        title="Alertes Immobilieres"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Search, title: "Surveillance automatique", description: "Scan quotidien ou hebdomadaire des nouvelles annonces" },
          { icon: Bell, title: "Notifications email", description: "Recois un email des qu'un bien correspond a tes criteres" },
          { icon: Zap, title: "900+ sources", description: "LeBonCoin, SeLoger, PAP et bien d'autres" },
        ]}
      />
    );
  }

  const fetchAlerts = async () => {
    try {
      const resp = await axios.get(`${API}/alerts/list`, { withCredentials: true });
      setAlerts(resp.data.alerts || []);
    } catch (error) {
      console.error('Alerts fetch error:', error);
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const createAlert = async () => {
    if (!form.name.trim() || !form.ville.trim()) {
      toast.error('Nom et ville sont requis');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        ville: form.ville,
        type_bien: form.type_bien,
        frequency: form.frequency,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        surface_min: form.surface_min ? parseFloat(form.surface_min) : null,
      };
      await axios.post(`${API}/alerts/create`, payload, { withCredentials: true });
      toast.success('Alerte creee !');
      setShowForm(false);
      setForm({ name: '', ville: '', type_bien: 'appartement', budget_min: '', budget_max: '', surface_min: '', frequency: 'daily' });
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
      toast.success('Alerte supprimee');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleAlert = async (alertId, currentActive) => {
    try {
      await axios.patch(`${API}/alerts/${alertId}`, { active: !currentActive }, { withCredentials: true });
      toast.success(currentActive ? 'Alerte desactivee' : 'Alerte activee');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const runAlertNow = async (alertId) => {
    setRunningId(alertId);
    try {
      const resp = await axios.post(`${API}/alerts/${alertId}/run`, {}, { withCredentials: true });
      const data = resp.data;
      if (data.new_count > 0) {
        toast.success(`${data.new_count} nouveau(x) bien(s) trouve(s) !`);
      } else {
        toast.info(`${data.total_found} biens trouves, aucun nouveau`);
      }
      fetchAlerts();
      if (expandedAlert === alertId) fetchHistory(alertId);
    } catch (error) {
      toast.error('Erreur lors du scan');
    } finally {
      setRunningId(null);
    }
  };

  const fetchHistory = async (alertId) => {
    try {
      const resp = await axios.get(`${API}/alerts/${alertId}/history`, { withCredentials: true });
      setHistoryData(prev => ({ ...prev, [alertId]: resp.data.results || [] }));
    } catch (error) {
      console.error('History fetch error:', error);
    }
  };

  const toggleExpand = (alertId) => {
    if (expandedAlert === alertId) {
      setExpandedAlert(null);
    } else {
      setExpandedAlert(alertId);
      if (!historyData[alertId]) fetchHistory(alertId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size="default" linkTo="/dashboard" />
              <div className="hidden md:flex items-center space-x-2 text-slate-400">
                <span>/</span>
                <span className="text-slate-900 font-medium">Alertes</span>
              </div>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" data-testid="back-to-dashboard">
                <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Dashboard</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white py-5 sm:py-8">
        <div className="max-w-5xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <BellRing className="w-5 h-5 sm:w-8 sm:h-8" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold" data-testid="alerts-title">Mes Alertes</h1>
                <p className="text-white/80 text-xs sm:text-base">Surveillance automatique des annonces</p>
              </div>
            </div>
            <div className="flex gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xl sm:text-3xl font-bold">{alerts.length}</div>
                <div className="text-[10px] sm:text-sm text-white/70">Alertes</div>
              </div>
              <div className="text-center hidden sm:block">
                <div className="text-3xl font-bold">{alerts.filter(a => a.active).length}</div>
                <div className="text-sm text-white/70">Actives</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8" data-testid="alerts-content">
        {/* Create button */}
        <div className="mb-6">
          <Button
            onClick={() => setShowForm(!showForm)}
            className={showForm ? 'bg-slate-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'}
            data-testid="create-alert-btn"
          >
            {showForm ? <><X className="w-4 h-4 mr-2" /> Annuler</> : <><Plus className="w-4 h-4 mr-2" /> Nouvelle alerte</>}
          </Button>
        </div>

        {/* Create form */}
        {showForm && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50" data-testid="create-alert-form">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg text-slate-800 mb-4">Creer une alerte</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Nom de l'alerte *</label>
                  <Input
                    placeholder="Ex: Lyon centre T2"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="alert-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Ville *</label>
                  <Input
                    placeholder="Ex: Lyon, Paris, Bordeaux..."
                    value={form.ville}
                    onChange={e => setForm({ ...form, ville: e.target.value })}
                    data-testid="alert-ville-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Type de bien</label>
                  <select
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={form.type_bien}
                    onChange={e => setForm({ ...form, type_bien: e.target.value })}
                    data-testid="alert-type-select"
                  >
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Frequence</label>
                  <select
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={form.frequency}
                    onChange={e => setForm({ ...form, frequency: e.target.value })}
                    data-testid="alert-freq-select"
                  >
                    {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Budget min</label>
                  <Input
                    type="number"
                    placeholder="Ex: 100000"
                    value={form.budget_min}
                    onChange={e => setForm({ ...form, budget_min: e.target.value })}
                    data-testid="alert-budget-min"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Budget max</label>
                  <Input
                    type="number"
                    placeholder="Ex: 250000"
                    value={form.budget_max}
                    onChange={e => setForm({ ...form, budget_max: e.target.value })}
                    data-testid="alert-budget-max"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1 block">Surface min (m2)</label>
                  <Input
                    type="number"
                    placeholder="Ex: 30"
                    value={form.surface_min}
                    onChange={e => setForm({ ...form, surface_min: e.target.value })}
                    data-testid="alert-surface-min"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={createAlert}
                    disabled={creating}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                    data-testid="submit-alert-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Creer l'alerte
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts list */}
        {alerts.length === 0 && !showForm ? (
          <div className="text-center py-16">
            <BellRing className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucune alerte configuree</h2>
            <p className="text-slate-600 mb-6">
              Cree une alerte pour etre notifie automatiquement quand de nouveaux biens correspondent a tes criteres.
            </p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-amber-500 to-orange-600"
              data-testid="empty-create-alert-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Creer ma premiere alerte
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <Card
                key={alert.alert_id}
                className={`transition-all ${alert.active ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-slate-300 opacity-70'}`}
                data-testid={`alert-card-${alert.alert_id}`}
              >
                <CardContent className="p-3 sm:p-4">
                  {/* Alert header */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{alert.name}</h3>
                        <Badge className={`${alert.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} text-[10px]`}>
                          {alert.active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {alert.frequency === 'daily' ? 'Quotidienne' : 'Hebdomadaire'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{alert.ville}</span>
                        <span className="flex items-center gap-1"><Home className="w-3 h-3" />{TYPE_OPTIONS.find(t => t.value === alert.type_bien)?.label || alert.type_bien}</span>
                        {alert.budget_max && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />max {parseInt(alert.budget_max).toLocaleString()}</span>}
                      </div>
                      {alert.last_run && (
                        <div className="text-xs text-slate-400 mt-1">
                          Dernier scan: {new Date(alert.last_run).toLocaleString('fr-FR')}
                          {alert.last_results_count > 0 && (
                            <span className="text-amber-600 font-medium ml-1">
                              ({alert.last_results_count} nouveau(x))
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost" size="sm"
                        className="h-8 text-xs"
                        onClick={() => runAlertNow(alert.alert_id)}
                        disabled={runningId === alert.alert_id}
                        data-testid="run-alert-btn"
                      >
                        {runningId === alert.alert_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className={`h-8 text-xs ${alert.active ? 'text-green-600' : 'text-slate-400'}`}
                        onClick={() => toggleAlert(alert.alert_id, alert.active)}
                        data-testid="toggle-alert-btn"
                      >
                        {alert.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-8 text-xs text-slate-400"
                        onClick={() => toggleExpand(alert.alert_id)}
                        data-testid="expand-alert-btn"
                      >
                        {expandedAlert === alert.alert_id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-8 text-xs text-red-500 hover:text-red-700"
                        onClick={() => deleteAlert(alert.alert_id)}
                        data-testid="delete-alert-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded history */}
                  {expandedAlert === alert.alert_id && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-1.5">
                        <History className="w-4 h-4" /> Historique des scans
                      </h4>
                      {(historyData[alert.alert_id] || []).length === 0 ? (
                        <p className="text-sm text-slate-400">Aucun scan effectue. Clique sur le bouton play pour lancer un scan.</p>
                      ) : (
                        <div className="space-y-3">
                          {(historyData[alert.alert_id] || []).slice(0, 5).map((run, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-slate-500">
                                  {new Date(run.run_at).toLocaleString('fr-FR')}
                                </span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px]">
                                    {run.total_found} trouve(s)
                                  </Badge>
                                  {run.new_count > 0 && (
                                    <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                      {run.new_count} nouveau(x)
                                    </Badge>
                                  )}
                                  {run.notified && (
                                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                      Email envoye
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {/* Show new listings from this run */}
                              {run.new_listings && run.new_listings.length > 0 && (
                                <div className="space-y-2 mt-2">
                                  {run.new_listings.slice(0, 3).map((listing, li) => (
                                    <div key={li} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white rounded p-2 border border-slate-100 gap-1">
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-800 truncate">{listing.titre}</div>
                                        <div className="text-xs text-slate-500">{listing.localisation}</div>
                                      </div>
                                      <div className="flex items-center gap-2 sm:gap-3 sm:ml-3">
                                        <span className="text-sm font-bold text-[#0D47A1]">{listing.prix?.toLocaleString()} EUR</span>
                                        {listing.surface > 0 && <span className="text-xs text-slate-500">{listing.surface}m2</span>}
                                        {listing.url && (
                                          <a href={listing.url} target="_blank" rel="noopener noreferrer">
                                            <ArrowUpRight className="w-3.5 h-3.5 text-[#0D47A1]" />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AlertsPage;
